<template>
  <div class="space-y-6">
    <!-- Dosya Yükleme Alanı -->
    <div class="border-2 border-dashed rounded-xl transition-all duration-300 ease-in-out p-6" :class="{
        'border-primary bg-primary/5 shadow-lg': isDragOver,
        'border-gray-300 hover:border-primary/30 hover:bg-gray-50': !isDragOver
      }" @dragenter.prevent="handleDragEnter" @dragleave.prevent="handleDragLeave" @dragover.prevent @drop.prevent="handleFileDrop">
      <input type="file" ref="fileInput" class="hidden" multiple @change="handleFileSelect" accept=".pdf,.doc,.docx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.pptx,.ppt"/>

      <div class="flex flex-col items-center justify-center py-6 px-4 text-center">
        <!-- Upload Icon Animation -->
        <div class="mb-4 relative">
          <div class="absolute -inset-4 rounded-full" :class="isDragOver ? 'bg-primary/10 animate-pulse' : ''"></div>
          <div class="relative bg-white p-4 rounded-full shadow-md border border-gray-100" :class="isDragOver ? 'border-primary/30 scale-110' : ''">
            <span class="material-symbols-outlined text-4xl transition-all duration-300" :class="isDragOver ? 'text-primary' : 'text-gray-400'">
              cloud_upload
            </span>
          </div>
        </div>

        <div class="max-w-md">
          <h3 class="text-lg font-semibold mb-2 transition-all duration-300" :class="{ 'text-primary': isDragOver, 'text-gray-700': !isDragOver }">
            Dosyalarınızı Yükeyin </h3>
          <p class="text-sm font-medium mb-3" :class="{ 'text-primary/80': isDragOver, 'text-gray-600': !isDragOver }">
            Dosyalarınızı buraya sürükleyin veya
            <button @click.prevent="$refs.fileInput.click()" class="px-2 py-1 ml-1 font-medium rounded-md transition-all duration-200 inline-flex items-center" :class="isDragOver ? 'bg-primary text-white hover:bg-primary-dark' : 'bg-primary/10 text-primary hover:bg-primary/20'">
              <span class="material-symbols-outlined text-sm mr-1">attach_file</span> Dosya Seçin
            </button>
          </p>
          <div class="flex flex-wrap justify-center gap-2 mb-3">
            <span class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center">
              <span class="material-symbols-outlined text-xs mr-1">description</span> PDF
            </span> <span class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center">
              <span class="material-symbols-outlined text-xs mr-1">description</span> Word
            </span> <span class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center">
              <span class="material-symbols-outlined text-xs mr-1">image</span> Görseller
            </span> <span class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center">
              <span class="material-symbols-outlined text-xs mr-1">slideshow</span> Sunumlar
            </span>
          </div>
          <p class="text-xs text-gray-500">
            Görseller için maks. 5MB, diğer dosyalar için toplam maks. 15MB </p>
        </div>
      </div>
    </div>

    <!-- Yüklenen Dosyalar Listesi -->
    <div v-if="files.length > 0" class="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <!-- Başlık ve butonlar -->
      <div class="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b flex items-center justify-between">
        <div class="flex items-center">
          <span class="material-symbols-outlined text-primary mr-2">folder_open</span>
          <h3 class="text-base font-semibold text-gray-800">Yüklemeye Hazır Dosyalar</h3>
          <span class="ml-2 px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">{{ files.length }}</span>
        </div>
        <button @click="toggleExpandAll" class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 border shadow-sm" :class="expandAll ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300' : 'bg-primary hover:bg-primary-dark text-white border-primary'">
          <span class="material-symbols-outlined !text-[18px]">
            {{ expandAll ? 'unfold_less' : 'unfold_more' }}
          </span> <span>{{ expandAll ? 'Tümünü Daralt' : 'Tümünü Genişlet' }}</span>
        </button>
      </div>

      <!-- Eksik Alan Uyarısı - Hemen başlığın altında -->
      <div v-if="hasEmptyFields" class="px-6 py-3 bg-amber-50 border-b border-amber-100">
        <p class="text-sm text-amber-700 flex items-center">
          <span class="material-symbols-outlined text-amber-500 mr-2">warning</span> Lütfen tüm dosyalar için zorunlu alanları doldurun (Kategori ve Dosya Türü) </p>
      </div>

      <!-- Dosyalar Listesi -->
      <TransitionGroup name="list" tag="ul" class="divide-y divide-gray-100">
        <li v-for="(file, index) in files" :key="file.id" class="transition-all duration-300">
          <!-- Dosya Satırı -->
          <div class="flex items-center justify-between px-6 py-4 transition-colors" :class="{
              'bg-white': !expandedFiles.includes(file.id) && !expandAll,
              'bg-gray-50': expandedFiles.includes(file.id) || expandAll,
              'hover:bg-gray-50': !expandedFiles.includes(file.id) && !expandAll
            }">
            <!-- Sol Kısım: Dosya Bilgisi -->
            <div class="flex items-center overflow-hidden cursor-pointer" @click="toggleFileExpand(file.id)">
              <div class="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full mr-3" :class="{
                  'bg-blue-100': isImageFile(file), 
                  'bg-orange-100': isPresentationFile(file),
                  'bg-green-100': !isImageFile(file) && !isPresentationFile(file)
                }">
                <span class="material-symbols-outlined" :class="{
                    'text-blue-500': isImageFile(file), 
                    'text-orange-500': isPresentationFile(file),
                    'text-green-500': !isImageFile(file) && !isPresentationFile(file)
                  }">
                  {{ isImageFile(file) ? 'image' : (isPresentationFile(file) ? 'slideshow' : 'description') }}
                </span>
              </div>

              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate" :title="file.name">{{ file.name }}</p>
                <p class="text-xs text-gray-500 inline-flex items-center">
                  <span class="material-symbols-outlined text-xs mr-1">straighten</span> {{ formatFileSize(file.file.size) }} </p>
              </div>
            </div>

            <!-- Sağ Kısım: Durum ve Aksiyonlar -->
            <div class="flex items-center ml-3 gap-2">
              <!-- Durum Etiketi -->
              <div v-if="!file.categoryIds || file.categoryIds.length === 0 || !file.fileType" class="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 whitespace-nowrap">
                <span class="inline-flex items-center">
                  <span class="material-symbols-outlined text-xs mr-1">warning</span>
                  {{ !file.categoryIds || file.categoryIds.length === 0 ? 'Kategori Seçin' : !file.fileType ? 'Dosya Türü Seçin' : 'Eksik Bilgi' }}
                </span>
              </div>
              <div v-else class="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 whitespace-nowrap">
                <span class="inline-flex items-center">
                  <span class="material-symbols-outlined text-xs mr-1">check_circle</span>
                  Hazır
                </span>
              </div>

              <!-- Aksiyon Butonları -->
              <div class="flex items-center gap-1">
                <button @click="removeFile(index)" class="p-1.5 rounded-full text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors" title="Dosyayı Kaldır">
                  <span class="material-symbols-outlined !text-[18px]">delete</span>
                </button>
                <button @click="toggleFileExpand(file.id)" class="p-1.5 rounded-full text-gray-500 hover:text-primary hover:bg-primary/5 transition-colors" :title="expandedFiles.includes(file.id) ? 'Daralt' : 'Genişlet'">
                  <span class="material-symbols-outlined !text-[18px] transition-transform duration-300" :class="{'rotate-180': expandedFiles.includes(file.id)}">
                    expand_more
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Form Alanları - Genişletildiğinde Gösterilir -->
          <div v-if="expandedFiles.includes(file.id) || expandAll" class="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <!-- Kategori Seçimi -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  <span class="flex items-center">
                    <span class="material-symbols-outlined text-primary text-sm mr-1">category</span>
                    Kategoriler
                    <span class="text-red-500 ml-1">*</span>
                  </span> </label>
                <CategoryPicker v-model="file.categoryIds" :categories="formattedCategoryOptions" placeholder="Kategorileri seçin..." @update:modelValue="updateFileCategories(file.id, $event)" :error="!file.categoryIds || file.categoryIds.length === 0 ? 'Kategori seçimi zorunludur' : ''"/>
              </div>

              <!-- Dosya Türü -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  <span class="flex items-center">
                    <span class="material-symbols-outlined text-primary text-sm mr-1">folder_special</span>
                    Dosya Türü
                    <span class="text-red-500 ml-1">*</span>
                  </span> </label>
                <CustomSelect v-model="file.fileType" :options="fileTypeOptions" placeholder="Tür seçin" class="w-full" :error="!file.fileType ? 'Dosya türü seçimi zorunludur' : ''"/>
              </div>

              <!-- Açıklama Alanı -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  <span class="flex items-center">
                    <span class="material-symbols-outlined text-primary text-sm mr-1">description</span>
                    Açıklama
                    <span class="text-gray-400 text-xs ml-1">(Opsiyonel)</span>
                  </span> </label>
                <CustomInput v-model="file.description" placeholder="Kısa bir açıklama girin..." type="text" class="w-full" @update:modelValue="updateDescription(index, $event)"/>
              </div>
            </div>
          </div>
        </li>
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import CustomSelect from '@/components/base/inputs/VSelect.vue'
import CustomInput from '@/components/base/inputs/VInput.vue'
import CategoryPicker from './CategoryPicker.vue'

import { formatFileSize } from '../utils'

const props = defineProps({
  files: {
    type: Array,
    required: true,
  },
  categoryOptions: {
    type: Array,
    required: true,
  },
  fileTypeOptions: {
    type: Array,
    required: true,
  },
  expandAll: {
    type: Boolean,
    default: false,
  },
  expandedFiles: {
    type: Array,
    default: () => [],
  },
  hasEmptyFields: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'remove-file',
  'update-description',
  'toggle-expand',
  'toggle-expand-all',
  'process-files',
  'update-categories',
])

const fileInput = ref(null)
const isDragOver = ref(false)

// Kategorileri düzgün formatta döndürme
const formattedCategoryOptions = computed(() => {
  // Çocukları olan bir ağaç yapısı oluştur
  return buildCategoryTree(props.categoryOptions)
})

// Kategori ağacı oluşturan yardımcı fonksiyon
const buildCategoryTree = (categories) => {
  // Önce ID'ye göre tüm kategorileri haritala
  const categoryMap = {}
  categories.forEach(cat => {
    categoryMap[cat.value] = {
      id: cat.value,
      label: cat.label,
      parentId: cat.parentId || null,
      children: [],
    }
  })

  // Şimdi ağacı oluştur
  const rootCategories = []
  categories.forEach(cat => {
    if (cat.parentId && categoryMap[cat.parentId]) {
      // Eğer parent'i varsa, parent'in child'larına ekle
      categoryMap[cat.parentId].children.push(categoryMap[cat.value])
    } else {
      // Parent'i yoksa veya geçersizse, kök kategorilere ekle
      rootCategories.push(categoryMap[cat.value])
    }
  })

  return rootCategories
}

// Kategori adını ID'ye göre bulma
const getCategoryName = (categoryId) => {
  const category = props.categoryOptions.find(c => c.value === categoryId)
  return category ? category.label : 'Bilinmeyen Kategori'
}

// Kategori kaldırma
const removeCategory = (file, categoryId) => {
  file.categoryIds = file.categoryIds.filter(id => id !== categoryId)
  emit('update-categories', file.id, file.categoryIds)
}

// Kategori güncelleme
const updateFileCategories = (fileId, categoryIds) => {
  emit('update-categories', fileId, categoryIds)
}

// Açıklama güncelleme
const updateDescription = (index, description) => {
  emit('update-description', index, description)
}

// Dosya türü yardımcı metodları
const isImageFile = (file) => {
  return file && file.file && file.file.type && file.file.type.startsWith('image/')
}

const isPresentationFile = (file) => {
  return file && file.file && (
      (file.file.name && (file.file.name.endsWith('.ppt') || file.file.name.endsWith('.pptx'))) ||
      (file.file.type && (
          file.file.type === 'application/vnd.ms-powerpoint' ||
          file.file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ))
  )
}

// Event handlers
const handleDragEnter = (event) => {
  event.preventDefault()
  isDragOver.value = true
}

const handleDragLeave = (event) => {
  event.preventDefault()
  if (!event.currentTarget.contains(event.relatedTarget)) {
    isDragOver.value = false
  }
}

const handleFileDrop = (event) => {
  event.preventDefault()
  isDragOver.value = false

  const droppedFiles = Array.from(event.dataTransfer.files)
  emit('process-files', droppedFiles)
}

const handleFileSelect = (event) => {
  const selectedFiles = Array.from(event.target.files)
  emit('process-files', selectedFiles)

  // Reset file input
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const removeFile = (index) => {
  emit('remove-file', index)
}

const toggleFileExpand = (fileId) => {
  emit('toggle-expand', fileId)
}

const toggleExpandAll = () => {
  emit('toggle-expand-all')
}
</script>

<style scoped>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>